import { FC, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from "@mui/material";

import {
  Claim,
  ClaimAppeal,
  ClaimLinkPreview,
  ClaimLinkRole,
  ClaimResubmissionOf,
} from "../../../../../types/claims";
import { apiService } from "../../../../../services/apiService";
import useNotifier from "../../../../../hooks/useNotifier";
import { EditErrorAlert } from "../record-payment/ClaimEditDialogParts";
import ClaimLinkPicker, {
  LinkSelection,
  isSelectionReady,
} from "./ClaimLinkPicker";
import LinkSummary from "./LinkSummary";
import {
  ClaimLinkError,
  ClaimLinkErrorCode,
  canTakeLinkRole,
  defaultLinkRole,
  parseClaimLinkError,
} from "./claimLinks";

interface LinkClaimDialogProps {
  open: boolean;
  claim: Claim;
  /** The claim's recorded actions: the one with an open C&R action is the original. */
  appeals: ClaimAppeal[];
  /** Set when this claim is already some claim's corrected one. */
  resubmissionOf?: ClaimResubmissionOf | null;
  /** Right after recording a correct and resubmit: this claim is the
   * original, and closing skips the (optional) link. */
  afterRecording?: boolean;
  onClose: () => void;
  onLinked: () => void;
}

type Step = "choose" | "confirm";

// Refusals about the chosen claim: back to choosing, with the message under it
const PICKER_CODES: ClaimLinkErrorCode[] = [
  "validation_error",
  "claim_not_found",
  "child_already_linked",
  "same_claim",
  "link_cycle",
];

/**
 * Admin-only: links the claim with the other side of its correct and
 * resubmit (REV-2629). The admin picks the role and the other claim; Review
 * shows the API's preview (REV-2657): "#ORIG resubmitted as #NEW" with the
 * original's success fee before and after; only confirming links.
 */
const LinkClaimDialog: FC<LinkClaimDialogProps> = ({
  open,
  claim,
  appeals,
  resubmissionOf,
  afterRecording = false,
  onClose,
  onLinked,
}) => {
  const theme = useTheme();
  const notifier = useNotifier();
  const preselectedRole: ClaimLinkRole = afterRecording
    ? "original"
    : defaultLinkRole(appeals);
  const availableRoles = (["original", "corrected"] as ClaimLinkRole[]).filter(
    (role) => canTakeLinkRole(role, appeals, resubmissionOf),
  );

  const [step, setStep] = useState<Step>("choose");
  const [role, setRole] = useState<ClaimLinkRole>(preselectedRole);
  const [selection, setSelection] = useState<LinkSelection | null>(null);
  const [preview, setPreview] = useState<ClaimLinkPreview | null>(null);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [error, setError] = useState<ClaimLinkError | null>(null);
  const [isReviewing, setIsReviewing] = useState(false);
  const [isLinking, setIsLinking] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep("choose");
    setRole(preselectedRole);
    setSelection(null);
    setPreview(null);
    setPickerError(null);
    setError(null);
    setIsReviewing(false);
    setIsLinking(false);
    // Only on open: a refreshed appeals list mustn't reset a choice in progress
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Refusals of the preview and of the link call
  const handleRefusal = (refusal: ClaimLinkError): void => {
    if (!selection) return;
    if (refusal.code === "different_patient") {
      // The API knows better: ask for the confirmation it wants
      setSelection({
        ...selection,
        candidate: { ...selection.candidate, differentPatient: true },
        confirmDifferentPatient: false,
      });
      setStep("choose");
      return;
    }
    if (PICKER_CODES.includes(refusal.code)) {
      setPickerError(refusal.fieldErrors.otherClaimNumber || refusal.message);
      setStep("choose");
      return;
    }
    setError(refusal);
  };

  const handleReview = async () => {
    if (!selection || !isSelectionReady(selection)) return;
    setIsReviewing(true);
    setError(null);
    setPickerError(null);
    try {
      const response = await apiService.previewLinkClaim(claim.id, {
        otherClaimNumber: selection.candidate.claimNumber,
        role,
        confirmDifferentPatient: selection.confirmDifferentPatient,
      });
      if (!response.success || !response.data) {
        handleRefusal(parseClaimLinkError(response));
        return;
      }
      if (
        response.data.differentPatient &&
        !selection.confirmDifferentPatient
      ) {
        // The preview doesn't refuse a different patient: ask before confirming
        handleRefusal({
          code: "different_patient",
          message: "",
          fieldErrors: {},
        });
        return;
      }
      setPreview(response.data);
      setStep("confirm");
    } finally {
      setIsReviewing(false);
    }
  };

  const handleLink = async () => {
    if (!selection) return;
    setIsLinking(true);
    setError(null);
    try {
      const response = await apiService.linkClaim(claim.id, {
        otherClaimNumber: selection.candidate.claimNumber,
        role,
        confirmDifferentPatient: selection.confirmDifferentPatient,
      });
      if (response.success) {
        notifier.showSuccess("Claims linked.");
        onLinked();
        onClose();
        return;
      }
      handleRefusal(parseClaimLinkError(response));
    } finally {
      setIsLinking(false);
    }
  };

  const handleClose = () => {
    if (isLinking || isReviewing) return;
    onClose();
  };

  const roleHint =
    role !== preselectedRole
      ? undefined
      : role === "original"
        ? "Preselected: this claim has the Correct and resubmit action."
        : "Preselected: this claim has no Correct and resubmit action to link.";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          fontSize: "19px",
        }}
      >
        {step === "confirm"
          ? "Confirm link"
          : afterRecording
            ? "Link the corrected claim"
            : "Link with other claim"}
        <Typography
          component="div"
          sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
        >
          Claim {claim.claimNumber}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Stays mounted on Confirm: Back keeps the choice and the loaded list */}
          <Box
            sx={{
              display: step === "choose" ? "flex" : "none",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
              {afterRecording
                ? "Action recorded. Optionally, link the corrected claim the payer processed as new, so its payments count toward this claim's action. You can also do it later."
                : "Link the original claim with the corrected one the payer processed as new, so the corrected claim's payments count toward the original's action."}
            </Typography>
            <ClaimLinkPicker
              claimId={claim.id}
              role={role}
              onRoleChange={
                afterRecording
                  ? undefined
                  : (next) => {
                      setRole(next);
                      setSelection(null);
                      setPickerError(null);
                      setError(null);
                    }
              }
              availableRoles={availableRoles}
              roleHint={roleHint}
              value={selection}
              onChange={(next) => {
                setSelection(next);
                setPickerError(null);
                setError(null);
              }}
              error={pickerError}
              disabled={isReviewing}
            />
          </Box>
          {step === "confirm" && preview && <LinkSummary preview={preview} />}
          {error && <EditErrorAlert message={error.message} />}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        {step === "choose" ? (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={isReviewing}
            >
              {afterRecording ? "Skip" : "Cancel"}
            </Button>
            <Button
              onClick={handleReview}
              variant="contained"
              disabled={!isSelectionReady(selection)}
              loading={isReviewing}
            >
              Review
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => {
                setError(null);
                setStep("choose");
              }}
              variant="outlined"
              disabled={isLinking}
            >
              Back
            </Button>
            <Button
              onClick={handleLink}
              variant="contained"
              disabled={!!error}
              loading={isLinking}
            >
              Confirm and link
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LinkClaimDialog;
